/**
 * Regra única do aviso "categoria pedida não resolvida".
 *
 * O aviso só é OMITIDO quando a categoria pedida corresponde exatamente ao
 * foco exclusivo atual do itinerário E o resultado final já contém pelo menos
 * uma paragem válida desse mesmo foco. Nesse caso a pesquisa secundária de
 * enriquecimento é apenas diagnóstico técnico e não desvaloriza a rota.
 *
 * Qualquer categoria adicional DIFERENTE do foco mantém o aviso: uma exigência
 * real da pessoa nunca pode desaparecer do ecrã.
 */

const normalize = (value?: string | null) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .trim()
    .toLowerCase();

export type UnresolvedCategoryInput = {
  /** Foco exclusivo da sessão/pedido atual (ex.: "sweets"). */
  categoryFocus?: string | null;
  /** Categoria adicional pedida (ex.: "cafe"). */
  requestedStopCategory?: string | null;
  /** Número de paragens válidas do foco presentes no resultado final. */
  focusStopsInResult: number;
};

export function shouldSuppressUnresolvedCategory(input: UnresolvedCategoryInput): boolean {
  const focus = normalize(input.categoryFocus);
  const requested = normalize(input.requestedStopCategory);
  if (!focus || !requested) return false;
  if (focus !== requested) return false;
  return input.focusStopsInResult > 0;
}

/**
 * Frase humana para o aviso. Nunca expõe a query interna: recebe apenas o
 * identificador de categoria devolvido pelo servidor ("cafe", "sweets", …)
 * ou "custom" quando a categoria não é conhecida.
 */
const SUBJECT: Record<string, Record<string, string>> = {
  cafe: { pt: "uma pastelaria ou café", en: "a pastry shop or café", es: "una pastelería o cafetería", fr: "une pâtisserie ou un café", it: "una pasticceria o un caffè", de: "eine Konditorei oder ein Café" },
  sweets: { pt: "pastelarias ou confeitarias de doçaria tradicional", en: "traditional sweet shops or pastry shops", es: "pastelerías o confiterías tradicionales", fr: "des pâtisseries traditionnelles", it: "pasticcerie tradizionali", de: "traditionelle Konditoreien" },
  restaurant: { pt: "um restaurante", en: "a restaurant", es: "un restaurante", fr: "un restaurant", it: "un ristorante", de: "ein Restaurant" },
  hotel: { pt: "um alojamento", en: "a place to stay", es: "un alojamiento", fr: "un hébergement", it: "un alloggio", de: "eine Unterkunft" },
  viewpoint: { pt: "um miradouro", en: "a viewpoint", es: "un mirador", fr: "un point de vue", it: "un punto panoramico", de: "einen Aussichtspunkt" },
  museum: { pt: "um museu", en: "a museum", es: "un museo", fr: "un musée", it: "un museo", de: "ein Museum" },
  monument: { pt: "um monumento", en: "a monument", es: "un monumento", fr: "un monument", it: "un monumento", de: "ein Denkmal" },
  church: { pt: "uma igreja", en: "a church", es: "una iglesia", fr: "une église", it: "una chiesa", de: "eine Kirche" },
  park: { pt: "um parque ou jardim", en: "a park or garden", es: "un parque o jardín", fr: "un parc ou jardin", it: "un parco o giardino", de: "einen Park oder Garten" },
  nature: { pt: "um espaço natural", en: "a nature spot", es: "un espacio natural", fr: "un espace naturel", it: "un'area naturale", de: "ein Naturgebiet" },
  water: { pt: "um rio, lago ou praia", en: "a river, lake or beach", es: "un río, lago o playa", fr: "une rivière, un lac ou une plage", it: "un fiume, lago o spiaggia", de: "einen Fluss, See oder Strand" },
  shopping: { pt: "uma zona de compras", en: "a shopping spot", es: "una zona de compras", fr: "un lieu de shopping", it: "una zona per lo shopping", de: "eine Einkaufsmöglichkeit" },
  nightlife: { pt: "um bar ou espaço noturno", en: "a bar or nightlife venue", es: "un bar o local nocturno", fr: "un bar ou lieu nocturne", it: "un bar o locale notturno", de: "eine Bar oder Nachtlocation" },
  market: { pt: "um mercado", en: "a market", es: "un mercado", fr: "un marché", it: "un mercato", de: "einen Markt" },
  custom: { pt: "o tipo de local que pediste", en: "the kind of place you asked for", es: "el tipo de lugar que pediste", fr: "le type de lieu demandé", it: "il tipo di luogo richiesto", de: "die gewünschte Art von Ort" },
};

const SENTENCE: Record<string, (subject: string) => string> = {
  pt: (s) => `Não encontrei ${s} perto do percurso.`,
  en: (s) => `We couldn't find ${s} near the route.`,
  es: (s) => `No encontramos ${s} cerca de la ruta.`,
  fr: (s) => `Nous n'avons pas trouvé ${s} près de l'itinéraire.`,
  it: (s) => `Non abbiamo trovato ${s} vicino al percorso.`,
  de: (s) => `Wir konnten ${s} in der Nähe der Route nicht finden.`,
};

export function unresolvedCategoryMessage(category: string, language = "pt"): string {
  const lang = (language || "pt").toLowerCase().slice(0, 2);
  const key = SUBJECT[normalize(category)] ? normalize(category) : "custom";
  const subject = SUBJECT[key][lang] ?? SUBJECT[key].en;
  const sentence = SENTENCE[lang] ?? SENTENCE.en;
  return sentence(subject);
}
